import React, { useEffect, useState, useRef } from 'react'

export default function Lightbox({ images, currentIndex, onClose, onNavigate }) {
  const [touchStart, setTouchStart] = useState(null)
  const [touchEnd, setTouchEnd] = useState(null)
  const overlayRef = useRef(null)

  // Minimum swipe distance in pixels
  const minSwipeDistance = 50

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') onNavigate((currentIndex + 1) % images.length)
      if (e.key === 'ArrowLeft') onNavigate((currentIndex - 1 + images.length) % images.length)
    }

    window.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden' // Prevent background scroll

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [currentIndex, images, onClose, onNavigate])

  const handleTouchStart = (e) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe) {
      // Next image
      onNavigate((currentIndex + 1) % images.length)
    } else if (isRightSwipe) {
      // Previous image
      onNavigate((currentIndex - 1 + images.length) % images.length)
    }
  }

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      onClose()
    }
  }

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/95 p-4 select-none"
    >
      {/* Top bar */}
      <div className="absolute top-4 left-0 right-0 flex items-center justify-between px-6 text-white z-10">
        <span className="text-sm font-medium">
          {currentIndex + 1} af {images.length}
        </span>
        <button
          onClick={onClose}
          className="p-2 text-white/80 hover:text-white transition-colors duration-200 focus:outline-none"
          aria-label="Luk visning"
        >
          <i className="fa-solid fa-xmark text-2xl"></i>
        </button>
      </div>

      {/* Main Image Container */}
      <div
        className="relative w-full max-w-5xl h-[75vh] flex items-center justify-center"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Left Arrow */}
        <button
          onClick={() => onNavigate((currentIndex - 1 + images.length) % images.length)}
          className="absolute left-2 md:left-4 z-10 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all duration-200 focus:outline-none hidden sm:flex items-center justify-center"
          aria-label="Forrige billede"
        >
          <i className="fa-solid fa-chevron-left text-xl"></i>
        </button>

        {/* Image */}
        <img
          src={images[currentIndex]}
          alt={`Galleri billede ${currentIndex + 1}`}
          className="max-w-full max-h-full object-contain rounded-lg shadow-2xl pointer-events-none transition-all duration-300"
        />

        {/* Right Arrow */}
        <button
          onClick={() => onNavigate((currentIndex + 1) % images.length)}
          className="absolute right-2 md:right-4 z-10 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all duration-200 focus:outline-none hidden sm:flex items-center justify-center"
          aria-label="Næste billede"
        >
          <i className="fa-solid fa-chevron-right text-xl"></i>
        </button>
      </div>

      {/* Mobile Swipe Hint */}
      <p className="mt-4 text-xs text-white/40 sm:hidden">
        <i className="fa-solid fa-hand-pointer mr-1"></i> Stryg til siden for at skifte billede
      </p>
    </div>
  )
}
